import type { DetallesDiscapacidad } from "./contextoInclusion";

/**
 * El documento: UN objeto con toda la hoja de vida.
 *
 * El builder viejo reparte lo mismo en ocho estados de React, el Form de antd y
 * dos claves de localStorage que se sincronizan entre si. Los ultimos seis
 * commits del repo son fixes de esa duplicidad: datos rancios que pisan a los
 * frescos, secciones que se mezclan entre hojas de vida, el contacto de otra HV
 * apareciendo tras guardar. Con una sola fuente esa clase de bug no existe.
 *
 * Las relaciones de Directus vienen ya resueltas: aqui no hay
 * `professional_experience_id` ni tablas pivote, solo listas.
 */

/** Telefono tal como lo guarda Directus: JSON serializado en una columna texto. */
export interface Telefono {
  code: string;
  value: string;
}

export interface Experiencia {
  /** Id de la fila en `professional_experience`. Negativo si aun no se guardo. */
  id: number;
  /** Id de la fila pivote, necesario para desvincularla al borrar. */
  recordId?: number;
  company_name: string;
  position: string;
  sector: string;
  /**
   * "YYYY-MM" o null. UNA representacion, no cuatro.
   *
   * El adapter viejo guarda cada fecha como ISO y como texto a la vez
   * (`start_date` + `start_date_text`), y luego cada pantalla elige cual mira.
   * De ahi salen las fechas que se mueven un mes al guardar, por zona horaria.
   */
  start_date: string | null;
  end_date: string | null;
  current_job: boolean;
  /** Viñetas separadas por "\n". */
  details: string;
  achievements: string;
}

export interface Estudio {
  id: number;
  recordId?: number;
  institution_name: string;
  title_name: string;
  start_year: string;
  year: string;
  is_current: boolean;
  format: string;
  country_name: string;
  formation_level: string;
}

export interface Idioma {
  id: number;
  recordId?: number;
  name: string;
  level: string;
}

export interface Otro {
  id: number;
  recordId?: number;
  section_name: string;
  section_text: string;
}

export interface CVDocument {
  id: number;
  session_id: string;

  // --- contacto ---
  name: string;
  last_names: string;
  email: string;
  phone: Telefono | null;
  city: string;
  open_to_remote: boolean;
  profession: string;
  age: number | null;
  links: string[];

  // --- contenido ---
  resume: string;
  /** Ordenadas de mas reciente a mas antigua. Invariante, no sugerencia. */
  experience: Experiencia[];
  education: Estudio[];
  skills: string[];
  soft_skills: string[];
  tools: string[];
  languages: Idioma[];
  others: Otro[];

  // --- inclusion (ya existe en el backend) ---
  /**
   * HV inclusiva: la persona decide mostrar que tiene una discapacidad.
   *
   * Activarla añade el simbolo de inclusion al documento. El tipo y el
   * certificado son OPCIONALES a proposito: son datos sensibles (Ley 1581,
   * art. 5) y mostrar el simbolo no obliga a nadie a detallar su condicion.
   */
  is_inclusive: boolean;
  disability_type: string | null;
  disability_certificate: boolean;
  /**
   * Contexto de inclusion: las respuestas a las preguntas de seguimiento del
   * tipo de discapacidad (ver contextoInclusion.ts). Va al final de la hoja, y
   * solo con la HV inclusiva activa. Todo opcional, como el tipo.
   */
  disability_details: DetallesDiscapacidad | null;

  // --- ids de las filas M2O, para poder actualizarlas sin volver a leer ---
  meta: {
    skillsId: number | null;
    softSkillsId: number | null;
    toolsId: number | null;
    profileLinkId: number | null;
  };
}

/** Documento en blanco, para el camino "no tengo hoja de vida". */
export const documentoVacio = (id: number, sessionId = ""): CVDocument => ({
  id,
  session_id: sessionId,
  name: "",
  last_names: "",
  email: "",
  phone: null,
  city: "",
  open_to_remote: false,
  profession: "",
  age: null,
  links: [],
  resume: "",
  experience: [],
  education: [],
  skills: [],
  soft_skills: [],
  tools: [],
  languages: [],
  others: [],
  is_inclusive: false,
  disability_type: null,
  disability_certificate: false,
  disability_details: null,
  meta: {
    skillsId: null,
    softSkillsId: null,
    toolsId: null,
    profileLinkId: null,
  },
});

/**
 * Las secciones del documento, en el orden en que se leen.
 *
 * Es el orden del documento Y el de agrupacion del panel de cambios: si fueran
 * dos listas distintas se desincronizarian a la primera seccion nueva.
 */
export const SECCIONES = [
  "contacto",
  "perfil",
  "experiencia",
  "educacion",
  "competencias",
  "idiomas",
  "otros",
] as const;

export type Seccion = (typeof SECCIONES)[number];

export const ETIQUETA_SECCION: Record<Seccion, string> = {
  contacto: "Datos personales",
  perfil: "Perfil profesional",
  experiencia: "Experiencia laboral",
  educacion: "Educación",
  competencias: "Competencias",
  idiomas: "Idiomas",
  otros: "Otros",
};
